import React, { useEffect, useState, useCallback } from "react";
import { Loader2, PackageOpen, Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, EmptyState, ScanBar } from "./fxui";
import { QrLabelButton } from "./QrLabel";

export default function FxPacking() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/fx/packing/queue"); setQueue(data.orders || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (code) => {
    try {
      const { data: scan } = await api.get("/fx/scan", { params: { code } });
      setActive(scan.order_number || code);
    } catch { toast.error("Not found"); }
  };

  return (
    <div data-testid="fx-packing">
      <ScanBar onSubmit={open} placeholder="Scan an order to pack…" testid="fx-pack-scan" />
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>
        : queue.length === 0 ? <EmptyState>Packing queue is empty. Orders appear here once picking is complete.</EmptyState>
        : (
        <div className="space-y-2">
          {queue.map((o) => (
            <button key={o.order_number} data-testid={`fx-pack-row-${o.order_number}`} onClick={() => setActive(o.order_number)}
              className="w-full text-left bg-white border border-black/5 rounded-2xl px-5 py-4 hover:border-[#306D29]/40 flex items-center gap-3"
              style={{ transition: "border-color 0.15s ease" }}>
              <PackageOpen size={18} className="text-[#306D29]" />
              <div className="flex-1">
                <p className="font-semibold text-[#0D530E]">{o.order_number}</p>
                <p className="text-xs text-[#5C5C5C]">{o.customer_name} · {o.items_total} items</p>
              </div>
              <span className="text-xs text-[#5C5C5C]">{o.packages} package(s)</span>
            </button>
          ))}
        </div>
      )}
      {active && <PackModal orderNumber={active} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function PackModal({ orderNumber, onClose }) {
  const [order, setOrder] = useState(null);
  const [ptype, setPtype] = useState("box");
  const [weight, setWeight] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/fx/orders/${orderNumber}`);
    setOrder(data);
  }, [orderNumber]);
  useEffect(() => { load(); }, [load]);

  const addPackage = async () => {
    await api.post("/fx/packages", { order_number: orderNumber, package_type: ptype, weight_kg: weight ? Number(weight) : null });
    toast.success("Package created"); setWeight(""); load();
  };
  const qc = async (pn) => { await api.put(`/fx/packages/${pn}/qc`, { qc_status: "passed" }); toast.success("QC passed"); load(); };
  const del = async (pn) => { await api.delete(`/fx/packages/${pn}`); load(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-[#FBF5DD] w-full max-w-lg h-full overflow-y-auto p-7" onClick={(e) => e.stopPropagation()} data-testid="fx-pack-modal">
        <h3 className="font-display text-3xl text-[#0D530E] mb-1">{orderNumber}</h3>
        {!order ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div> : (
          <>
            <p className="text-sm text-[#5C5C5C] mb-5">{order.customer.name} · {order.items.length} items</p>
            <Card className="mb-5">
              <p className="text-sm font-semibold mb-2">Items to pack</p>
              {order.items.map((it) => (
                <div key={it.key} className="text-sm flex justify-between py-1">
                  <span>{it.name} × {it.qty_found ?? it.quantity}</span>
                  <span className="text-[#5C5C5C] text-xs">{it.pick_status}</span>
                </div>
              ))}
            </Card>

            <div className="flex gap-2 mb-4">
              <select value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="fx-pack-type"
                className="bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm">
                <option value="box">Box</option><option value="bag">Bag</option><option value="pallet">Pallet</option>
              </select>
              <input value={weight} onChange={(e) => setWeight(e.target.value)} type="number" placeholder="Weight (kg)" data-testid="fx-pack-weight"
                className="flex-1 bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm" />
              <button onClick={addPackage} data-testid="fx-pack-add"
                className="bg-[#306D29] text-white px-4 rounded-xl text-sm font-semibold flex items-center gap-1"><Plus size={15} /> Package</button>
            </div>

            <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold mb-2">Packages</p>
            <div className="space-y-2">
              {order.packages.map((p) => (
                <div key={p.package_number} className="bg-white border border-black/5 rounded-xl px-4 py-3 flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{p.package_number}</p>
                    <p className="text-xs text-[#5C5C5C]">{p.package_type} {p.weight_kg ? `· ${p.weight_kg}kg` : ""}</p>
                  </div>
                  {p.qc_status === "passed" ? <span className="text-[#306D29] text-xs font-semibold flex items-center gap-1"><ShieldCheck size={14} /> QC passed</span>
                    : <button onClick={() => qc(p.package_number)} data-testid={`fx-pack-qc-${p.package_number}`} className="text-xs font-semibold bg-[#306D29] text-white px-3 py-1.5 rounded-full">Pass QC</button>}
                  <QrLabelButton code={p.package_number} title={orderNumber} subtitle={order.customer.name} testid={`fx-pack-label-${p.package_number}`} label="Label" />
                  <button onClick={() => del(p.package_number)} className="text-[#B23B3B] w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#B23B3B]/10"><Trash2 size={14} /></button>
                </div>
              ))}
              {order.packages.length === 0 && <p className="text-sm text-[#5C5C5C]">No packages yet.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
